import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingPage from '../components-library/loading/LoadingPage';
import OnSiteTableManager, { OnSiteTableLink, OnSiteTableLinkState } from '../services/api/OnSiteTableManager';
import pageDiagnostics from '../services/helpers/PageDiagnostics';
import { ORDER_METHODS } from '../services/exports/Constants';
import { actionCreators } from '../store/actions';
import { StoreInterface } from '../store/types';
import useCompanySlug from '../hooks/global/useCompanySlug';
import useInitialData from '../hooks/global/useInitialData';
import useOrderFlow from '../hooks/order/useOrderFlow';
import useRouter from '../hooks/navigation/useRouter';
import useAnalytics from '../hooks/analytics/useAnalytics';
import { TABLE_LINK_NOTICE_PARAM, TableLinkNotice } from '../utils/on-site';
import { setTableNotice } from '../store/slices/OrderMethodSlice';

const NOT_FOUND = 404;

/**
 * How long each stage of the link waits before it gives up and says so: the shop's own load (until
 * its franchise is stored, after its pictures preload), then the link's resolve and its location's
 * load (until the table is applied). Generous: a first scan on slow restaurant Wi-Fi is still a scan.
 */
export const TABLE_LINK_STAGE_TIMEOUT_MS = 25_000;

/** `table_link_opened` states besides the resolver's own: an unknown link, or one that couldn't be resolved. */
export enum TableLinkOpenedOutcome {
  NotFound = 'not_found',
  Failed = 'failed',
}

/**
 * `/t/:uuid`, the link in a table's QR code (PROD-724). It opens the menu of the table's location
 * with table ordering and that table chosen. The link wins over the guest's last order method and
 * location. A table that was deleted or switched off, or a restaurant that stopped table ordering,
 * opens the menu in pickup with a note; an unknown link (404) opens the shop as usual. Any other
 * failure, the resolver's or the location's load, opens the shop with a note to scan again, and is
 * reported: restaurant Wi-Fi puts many guests behind one IP, so a run of them is the resolver's throttle.
 * A stage that never settles (the shop's load, or the resolver or the location's load once resolving
 * started) gives up after TABLE_LINK_STAGE_TIMEOUT_MS the same way.
 * Every scan is recorded (`table_link_opened`): with the resolver's state, `not_found` or `failed`.
 */
export default function TableLinkScreen() {
  const { uuid } = useParams();
  const franchiseId = useSelector((state: StoreInterface) => state.initialData.franchise?.id);
  const franchiseIdRef = useRef(franchiseId);
  franchiseIdRef.current = franchiseId;
  const company = useSelector((state: StoreInterface) => state.initialData.company);
  const companyRef = useRef(company);
  companyRef.current = company;
  const { updateSlug, loading: slugLoading } = useCompanySlug();
  const { forceRefresh, loading: appLoading } = useInitialData();
  const { setOrderMethodInfoInfoComplete } = useOrderFlow();
  const { navigate } = useRouter();
  const { trackTableLinkOpened } = useAnalytics();
  const dispatch = useDispatch();
  const started = useRef(false);
  const applied = useRef(false);
  // The link has opened the shop (or given up): whatever is still in flight is ignored.
  const settled = useRef(false);
  const tracked = useRef(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The restaurant as it was in the store when the link resolved: a copy saved in the browser can
  // predate the table, or table ordering itself, so only a copy loaded after that counts.
  const staleCompany = useRef<typeof company>(undefined);
  const [link, setLink] = useState<OnSiteTableLink>(null);

  // The shop's load can fail without ever settling (its franchise never arrives): rather than a
  // spinner forever, the shop opens with the note to scan again. Before the effect below, whose
  // resolve() starts the next stage's clock.
  useEffect(() => {
    giveUpAfterStage();

    return () => clearTimeout(timeout.current);
  }, []);

  // Waits for the route's own load of the shop, including the app's loading state, which turns off a
  // moment after the request: a load started inside that moment toggles it out of step, and
  // CompanySlugObserver unmounts this screen mid-way. (An older load landing last is dropped by
  // InitialLoadingManager.)
  useEffect(() => {
    if (!franchiseId || slugLoading || appLoading || started.current) {
      return;
    }

    started.current = true;
    resolve();
  }, [franchiseId, slugLoading, appLoading]);

  useEffect(() => {
    if (
      !link ||
      settled.current ||
      applied.current ||
      !company?.id ||
      company === staleCompany.current ||
      company.id !== link.company.id
    ) {
      return;
    }

    applied.current = true;
    apply(link);
  }, [link, company]);

  // Each stage gets the whole budget: a slow first load doesn't eat into the resolve's.
  function giveUpAfterStage(): void {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      if (settled.current) {
        return;
      }

      const event = started.current ? 'resolve_timeout' : 'shop_load_timeout';
      started.current = true;
      pageDiagnostics.tableLink(event, { uuid, franchise_id: franchiseIdRef.current ?? null });
      // A link that resolved was recorded with its state already.
      track(TableLinkOpenedOutcome.Failed);
      fail();
    }, TABLE_LINK_STAGE_TIMEOUT_MS);
  }

  // One `table_link_opened` per scan.
  function track(outcome: string): void {
    if (tracked.current) {
      return;
    }

    tracked.current = true;
    trackTableLinkOpened(outcome);
  }

  async function resolve(): Promise<void> {
    giveUpAfterStage();

    let request = await OnSiteTableManager.resolve(uuid, franchiseId);

    // A network blip (no status) or a server error shouldn't lose the table: one more try. A 404 is
    // an unknown link and stays one; other 4xx answers won't change either.
    if (!settled.current && !request.success && (!request.status || request.status >= 500)) {
      request = await OnSiteTableManager.resolve(uuid, franchiseId);
    }

    // Timed out meanwhile: the shop is already open with the note.
    if (settled.current) {
      return;
    }

    if (!request.success && request.status === NOT_FOUND) {
      track(TableLinkOpenedOutcome.NotFound);

      return open('/');
    }

    const resolved = request.success ? request.response?.data?.data : null;

    if (!resolved?.company?.slug) {
      pageDiagnostics.tableLink('resolve_failed', { uuid, status: request.status ?? null, franchise_id: franchiseId });
      track(TableLinkOpenedOutcome.Failed);

      return fail();
    }

    track(resolved.state);

    // The copy of the restaurant in the store already lists this table: it is fresh enough, and a
    // reload would only repeat the route's own load. Otherwise the location is (re)loaded, and the
    // copy from before the load is not trusted with the table.
    const listed =
      companyRef.current?.id === resolved.company.id &&
      companyRef.current.on_site_tables?.some((item) => item.uuid === resolved.table?.uuid);

    if (!listed) {
      staleCompany.current = companyRef.current;
    }

    setLink(resolved);

    // updateSlug loads the location unless it is already the current slug; then it is loaded here,
    // unless the copy in the store is fresh enough.
    const loaded = await updateSlug(resolved.company.slug);
    const landed = loaded === undefined ? listed || (await forceRefresh(resolved.company.slug, true))?.success : loaded;

    // The location couldn't be loaded: the shop opens as usual, with a note, rather than waiting forever.
    if (!landed && !applied.current && !settled.current) {
      applied.current = true;
      pageDiagnostics.tableLink('location_load_failed', {
        uuid,
        company_id: resolved.company.id,
        state: resolved.state,
      });

      return fail();
    }
  }

  function apply(resolved: OnSiteTableLink): void {
    // The link names the table by its uuid; orders send its id, from the restaurant's tables.
    const table = (company?.on_site_tables ?? []).find((item) => item.uuid === resolved.table?.uuid);
    // A reason from earlier in the visit is out of date: this link gives its own (in the URL) or none.
    dispatch(setTableNotice(null));

    if (resolved.state === OnSiteTableLinkState.Ok && !table) {
      pageDiagnostics.tableLink('table_not_in_company', { uuid, company_id: resolved.company.id });
    }

    if (resolved.state !== OnSiteTableLinkState.Ok || !table) {
      dispatch(actionCreators.order.updateOrder({ method: ORDER_METHODS.PICKUP, on_site_table: null }));
      // The link chose the location, so the note is read on the menu, not under the order-method modal.
      setOrderMethodInfoInfoComplete();

      const notice =
        resolved.state === OnSiteTableLinkState.OnSiteUnavailable
          ? TableLinkNotice.TableOrderingOff
          : TableLinkNotice.TableUnavailable;

      return open(`/${resolved.company.slug}?${TABLE_LINK_NOTICE_PARAM}=${notice}`);
    }

    dispatch(
      actionCreators.order.updateOrder({
        company_id: resolved.company.id,
        method: ORDER_METHODS.ON_SITE,
        scheduled_for: null,
        on_site_table: {
          id: table.id,
          uuid: table.uuid,
          name: table.name,
          company_id: resolved.company.id,
          scanned_at: new Date().toISOString(),
        },
      }),
    );
    // The table alone makes the order complete (the basket and the modal gate check it); the tab's
    // "order type chosen" flag is left alone, so that when the cart leaves its table later the shop
    // asks for the order type again instead of checking out a pickup order nobody chose.

    return open(`/${resolved.company.slug}`);
  }

  // The link failed for a reason a new scan may fix: the shop opens as usual and the menu says so.
  function fail(): void {
    if (settled.current) {
      return;
    }

    dispatch(setTableNotice(TableLinkNotice.LoadFailed));

    return open('/');
  }

  // A string path: useNavigate keeps `absolute` and `replace` only for those, and the link must leave
  // no /t/… entry in the history.
  function open(path: string): void {
    if (settled.current) {
      return;
    }

    settled.current = true;
    clearTimeout(timeout.current);
    navigate({ web: { to: path, options: { absolute: true, replace: true } } });
  }

  return <LoadingPage />;
}
